import React, { useCallback } from 'react';
import { usePricingFormContext } from './PricingFormContext';
import {Col, Row, Layout,Checkbox, Divider,InputNumber, Select, Button, message, Modal} from 'antd';

// 检查数值的小数位数是否超过2位
const checkDecimalPlaces = (value: number | null | undefined, fieldName: string): boolean => {
    if (value === null || value === undefined) {
        return true;
    }
    const str = value.toString();
    const decimalIndex = str.indexOf('.');
    if (decimalIndex === -1) {
        return true; // 没有小数部分，通过
    }
    const decimalPart = str.substring(decimalIndex + 1);
    if (decimalPart.length > 2) {
        message.warning(`${fieldName}最多支持两位小数，当前输入超过限制`);
        return false;
    }
    return true;
};

// 价格必须 <= 0 的费用项 code 集合（TT预分拣费（负数）1001001060）
export const NEGATIVE_ONLY_FEE_ITEM_CODES = new Set<number>([1001001060]);

// 按费用项 code 校验价格符号：负数费用项要求 <= 0，其余费用项维持 >= 0
const checkPriceSign = (value: number | null | undefined, feeItemCodeNum?: number): boolean => {
    if (value === null || value === undefined || feeItemCodeNum === undefined) {
        return true;
    }
    const mustBeNonPositive = NEGATIVE_ONLY_FEE_ITEM_CODES.has(feeItemCodeNum);
    if (mustBeNonPositive && value > 0) {
        message.warning('该费用项金额仅允许小于等于0');
        return false;
    }
    if (!mustBeNonPositive && value < 0) {
        message.warning('金额不能为负数');
        return false;
    }
    return true;
};

const headerStyle: React.CSSProperties = {
    color: '#090909ff',
    lineHeight: '25px',
    backgroundColor: '#fff',
    paddingLeft: '0px',
    paddingRight: '0px',
};
const dividerStyle: React.CSSProperties = {
    margin: '15px',
};

export interface WeightRow {
    startWeight?: number | null;
    endWeight?: number | null;
    weightUnit?: string;
    priceUsd?: number | null;
}

export interface DeliveryFeeCard {
    zones: string[];
    weightRows: WeightRow[];
}

export interface DeliveryFeeData {
    cards: DeliveryFeeCard[];
}

const ItemRow : React.FC<{
    rowIndex: number;
    row: WeightRow;
    lastRow: boolean;
    onUpdate: (index: number, row: WeightRow) => void;
    onAdd: () => void;
    onDelete: () => void;
    disabled?: boolean;
    feeItemCodeNum?: number;
}> = ({ rowIndex, row, lastRow, onUpdate, onAdd, onDelete, disabled = false, feeItemCodeNum }) => {
    const units = [
        {value:'oz',label:'oz/盎司'}, 
        {value:'lb',label:'lb/英磅'}
    ];
    return (
        <Col span={24}>
            <Row>
                <Col span={18} >
                    <InputNumber 
                        size="small" 
                        style={{width:78}} 
                        value={row.startWeight ?? undefined}
                        disabled={disabled}
                        onChange={(v) => {
                            if (v !== null && v !== undefined) {
                                checkDecimalPlaces(v, '起始重量');
                            }
                            onUpdate(rowIndex, {...row, startWeight: v ?? null});
                        }}
                        placeholder="起始"
                    /> 
                    {' '}-{' '}
                    <InputNumber 
                        size="small" 
                        style={{width:78}} 
                        value={row.endWeight ?? undefined}
                        disabled={disabled}
                        onChange={(v) => {
                            if (v !== null && v !== undefined) {
                                checkDecimalPlaces(v, '结束重量');
                            }
                            onUpdate(rowIndex, {...row, endWeight: v ?? null});
                        }}
                        placeholder="结束"
                    /> 
                    {' '}&nbsp;&nbsp;单位&nbsp;
                    <Select  
                        style={{ width: 90}} 
                        options={units} 
                        size="small"
                        value={row.weightUnit}
                        disabled={disabled}
                        onChange={(v) => onUpdate(rowIndex, {...row, weightUnit: v})}
                    />
                    <Button type="primary" size={'small'} style={{marginLeft:5}} danger onClick={onDelete} disabled={disabled}>-</Button>
                    {lastRow && (
                        <Button type="primary" size={'small'} style={{marginLeft:5}} onClick={onAdd} disabled={disabled}>+</Button>
                    )}
                </Col>
                <Col span={6}>
                    <InputNumber 
                        size="small" 
                        style={{width:65, marginLeft:20}} 
                        value={row.priceUsd ?? undefined}
                        disabled={disabled}
                        onChange={(v) => {
                            if (v !== null && v !== undefined) {
                                checkDecimalPlaces(v, '金额');
                                // 负数费用项输入正数时只提示、保留输入值，由保存校验拦截（不再用 max 截断为 0）
                                const isNegativeOnly = feeItemCodeNum !== undefined && NEGATIVE_ONLY_FEE_ITEM_CODES.has(feeItemCodeNum);
                                if (!checkPriceSign(v, feeItemCodeNum) && !isNegativeOnly) return;
                            }
                            onUpdate(rowIndex, {...row, priceUsd: v ?? null});
                        }}
                    /> 
                    {' '}美金
                </Col>
            </Row>
        </Col>
    );
}

const PriceCard: React.FC<{
    cardIndex: number;
    card: DeliveryFeeCard;
    onUpdate: (index: number, card: DeliveryFeeCard) => void;
    onDelete: () => void;
    onCopy: () => void;
    showWeight?: boolean;
    disabled?: boolean;
    zoneOptions?: Array<{ label: string; value: string }>;
    feeItemCodeNum?: number;
}> = ({ cardIndex, card, onUpdate, onDelete, onCopy, showWeight = true, disabled = false, zoneOptions = [], feeItemCodeNum }) => {
    const { Header, Sider, Content } = Layout;
    const CheckboxGroup = Checkbox.Group;
    const weightRows = card.weightRows ?? [{ weightUnit: 'lb' }];

    const handleZoneChange = (zones: string[]) => {
        onUpdate(cardIndex, {...card, zones});
    };

    const handleAddRow = () => {
        onUpdate(cardIndex, {
            ...card,
            weightRows: [...weightRows, {weightUnit: 'lb'}]
        });
    };

    const handleDeleteRow = (rowIndex: number) => {
        if (weightRows.length > 1) {
            onUpdate(cardIndex, {
                ...card,
                weightRows: weightRows.filter((_: WeightRow, i: number) => i !== rowIndex)
            });
        }
    };

    const handleUpdateRow = (rowIndex: number, row: WeightRow) => {
        const newRows = [...weightRows];
        newRows[rowIndex] = row;
        onUpdate(cardIndex, {...card, weightRows: newRows});
    };

    return (
        <Layout style={{backgroundColor:'#fff', padding:15, border:'1px solid #e8e8e8', borderRadius:5}}>
            <Header style={headerStyle}>
                <Row justify="center" align='middle'>
                    <Col span={showWeight ? 6 : 20}>
                        <span>Zone/分区</span>
                    </Col>
                    {showWeight && (
                        <>
                            <Col span={14} >
                                <span>Weight/重量</span>
                            </Col>
                            <Col span={4} >
                                <span>Price/价格</span>
                            </Col>
                        </>
                    )}
                </Row>
                <Divider style={dividerStyle} />
            </Header>
            <Layout>
                <Sider width={showWeight ? "16%" : "100%"} theme='light'>
                    <CheckboxGroup 
                        options={zoneOptions.length > 0 ? zoneOptions : []} 
                        value={card.zones || []} 
                        onChange={handleZoneChange}
                        disabled={disabled}
                    />
                </Sider>
                {showWeight && (
                    <Content style={{ background: '#fff'}}>
                        <Row justify="start" gutter={[0, 7]}>
                            {weightRows.map((row, index) => (
                                <ItemRow 
                                    key={index}
                                    rowIndex={index}
                                    row={row}
                                    lastRow={index === weightRows.length - 1}
                                    onUpdate={handleUpdateRow}
                                    onAdd={handleAddRow}
                                    onDelete={() => handleDeleteRow(index)}
                                    disabled={disabled}
                                    feeItemCodeNum={feeItemCodeNum}
                                />
                            ))}
                        </Row>
                    </Content>
                )}
            </Layout>
            <Row justify="end" style={{marginTop:10}}>
                <Col>
                    <Button type="default" size="small" onClick={onCopy} disabled={disabled} style={{marginRight: 8}}>复制</Button>
                    <Button type="primary" danger size="small" onClick={onDelete} disabled={disabled}>删除</Button>
                </Col>
            </Row>
        </Layout>
    );
};

const DeliveryFeeTabb: React.FC<{
    tabId: string;
    data?: DeliveryFeeData;
    onChange?: (data: DeliveryFeeData) => void;
    feeItemCodeNum?: number;
    onDataChange?: (code: number, data: DeliveryFeeData) => void;
    showWeight?: boolean;
    disabled?: boolean;
    zoneOptions?: Array<{ label: string; value: string }>;
}> = ({ tabId, data: dataProp, onChange, feeItemCodeNum, onDataChange, showWeight = true, disabled = false, zoneOptions = [] }) => {
    const ctx = usePricingFormContext();
    const data = dataProp ?? (feeItemCodeNum != null ? ctx?.feeItemDataMap?.get(feeItemCodeNum) : undefined);
    const cards = data?.cards || [{zones: [], weightRows: [{weightUnit: 'lb'}]}];
    const notify = useCallback((payload: DeliveryFeeData) => {
        if (onDataChange != null && feeItemCodeNum != null) onDataChange(feeItemCodeNum, payload);
        else onChange?.(payload);
    }, [onDataChange, onChange, feeItemCodeNum]);

    const handleAddCard = () => {
        const newCards = [...cards, {zones: [], weightRows: [{weightUnit: 'lb'}]}];
        notify({cards: newCards});
    };

    const handleDeleteCard = (index: number) => {
        if (cards.length > 1) {
            Modal.confirm({
                title: '确认删除',
                content: '确定要删除这个价格卡片吗？删除后无法恢复。',
                okText: '确认',
                cancelText: '取消',
                okType: 'danger',
                onOk: () => {
                    const newCards = cards.filter((_: DeliveryFeeCard, i: number) => i !== index);
                    notify({cards: newCards});
                }
            });
        }
    };

    const handleUpdateCard = (index: number, card: DeliveryFeeCard) => {
        const newCards = [...cards];
        newCards[index] = card;
        notify({cards: newCards});
    };

    const handleCopyCard = (index: number) => {
        const cardToCopy = cards[index];
        const rows = cardToCopy?.weightRows ?? [];
        const copiedCard: DeliveryFeeCard = {
            zones: [...(cardToCopy?.zones || [])],
            weightRows: rows.map((row: WeightRow) => ({
                startWeight: row.startWeight,
                endWeight: row.endWeight,
                weightUnit: row.weightUnit,
                priceUsd: row.priceUsd
            }))
        };
        const newCards = [...cards];
        newCards.splice(index + 1, 0, copiedCard);
        notify({cards: newCards});
    };

    return (
        <div>
            {cards.map((card: DeliveryFeeCard, index: number) => (
                <div key={index} style={{ marginBottom: index !== cards.length - 1 ? 15 : 0 }}>
                    <PriceCard 
                        cardIndex={index}
                        card={card}
                        onUpdate={handleUpdateCard}
                        onDelete={() => handleDeleteCard(index)}
                        onCopy={() => handleCopyCard(index)}
                        showWeight={showWeight}
                        disabled={disabled}
                        zoneOptions={zoneOptions}
                        feeItemCodeNum={feeItemCodeNum}
                    />
                </div>
            ))}
            <Row justify="end" style={{marginTop:15}}>
                <Col>
                    <Button type="primary" size="middle" onClick={handleAddCard} disabled={disabled}>新增</Button>
                </Col>
            </Row>
        </div>
    );
}

export default React.memo(DeliveryFeeTabb);